export type AppMeta = {
    readonly allowed: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly metadata: any | null
};
export type Account = {
    readonly name: string
    readonly pcode: string
    readonly config: { readonly [app_name: string]: AppMeta }
};

export enum Application {
    AUDIENCE_PLANNER = 'audience_planner',
    CUSTOM_AUDIENCES = 'custom_audiences',
    CAMPAIGN_MANAGER = 'campaign_manager',
    PUBLISHER_CAMPAIGN_REPORTING = 'publisher_campaign_reporting',
    PUBLISHER_CAMPAIGN_REPORTING_COMPARISON = 'publisher_campaign_reporting_comparison',
    PUB_TARGETING = 'pub_targeting',
    CONTENT_SEGMENTS = 'content_segments',
    CONTENT_SEGMENTS_MANAGER = 'content_segment_manager',
    PROPERTY_UNLIMITED = 'property_unlimited',
    REPORTING = 'reporting',
    SHOW_AUDIENCE_GRID_ON_CONTENT_SEGMENTS = 'show_audience_grid_on_content_segments',
    Q_FOR_MARKETERS = 'q_for_marketers',
    QUANTCAST_CHOICE_PREMIUM = 'quantcast_choice_premium',
    QUANTCAST_CONSENTED_IDENTITY = 'quantcast_consented_identity',
    PLANNER_ONLY_FOR_MARKETERS = 'planner_only_for_marketers'
}

export default class AccountService {
    private urlBase: string
    private static readonly accountMap: { [pcode: string]: Account } = {}
    private static readonly accountActionsMap: { [pcode: string]: readonly string[] } = {}
    constructor() {
        this.urlBase = '/accountservice/api'
    }
    setBase(urlBase: string) {
        this.urlBase = urlBase
    }
    /**
     * Fetch and return account information. This function applies caching to the account information
     * @param pcode pcode with prefix of p-
     */
    getAccount(pcode: string): Promise<Account> {
        if (AccountService.accountMap[pcode]) {
            return Promise.resolve(AccountService.accountMap[pcode]);
        }
        return this.api<Account>(`${this.urlBase}/v3/accounts/${pcode}`)
            .then(account => {
                AccountService.accountMap[pcode] = account
                return account
            })
    }
    /**
     * Fetch and return account actions user can perform.
     * @param pcode pcode with prefix of p-
     */
    getAccountActions(pcode: string): Promise<readonly string[]> {
        if (AccountService.accountActionsMap[pcode]) {
            return Promise.resolve(AccountService.accountActionsMap[pcode]);
        }
        return this.api<readonly string[]>(`${this.urlBase}/v3/accounts/${pcode}/actions`)
            .then(actions => {
                AccountService.accountActionsMap[pcode] = actions
                return actions
            })
    }
    /**
     * Fetch application data.
     * @param pcode pcode with prefix of p-
     */
    getAppData(pcode: string, appName: Application): Promise<AppMeta | null> {
        return this.getAccount(pcode).then(acc => {
            return acc.config[appName]
        })
    }

    private api<T>(url: string): Promise<T> {
        return fetch(url, {
            credentials: "same-origin",
        }).then(response => {
            if (!response.ok) {
                throw new Error(response.statusText)
            }
            return response.json()
        })
    }
}
